import { useState } from 'react';
import cx from 'classnames';
import config from '@plone/volto/registry';
import { defineMessages, useIntl } from 'react-intl';
import {
  Button,
  ListBox,
  ListBoxItem,
  Popover,
  Select,
  SelectValue,
} from 'react-aria-components';

import styles from './styles.module.css';
import QubicaBaseCard from '../Common/QubicaBaseCard';
import type { QubicaContentProps, QubicaViewProps } from '../Common/utils';
import type { NationViewProps } from '../NationView/NationView';
import CardsSection from '../Common/CardsSection';
import { representativeSections } from '../Common/utils';
import QubicaBaseFooter from './Components/QubicaBaseFooter';
import ChevronDownSVG from '../../Icons/ChevronDownSVG';

export interface QubicaBaseViewProps extends QubicaViewProps {
  content: QubicaContentProps & {
    nation?: NationViewProps['content'][];
    body?: { 'content-type': string; data: string; encoding: string };
    body_local_information?: {
      'content-type': string;
      data: string;
      encoding: string;
    };
    localtitle?: string;
    language: { title: string; token: string };
  };
}

const QubicaBaseView = (props: QubicaBaseViewProps) => {
  const intl = useIntl();
  const Container = config.getComponent('Container').component || 'div';
  const Image = config.getComponent('Image').component;
  const { content } = props;

  const languages = content.localtitle
    ? [content.language?.title, content.localtitle]
    : [];

  const [selectedLocale, setSelectedLocale] = useState(content.language.title);
  return (
    <Container className={styles.qubicaBaseContainer}>
      {content.image && (
        <div
          className={cx('has--fullwidth--true', styles.headerImageContainer)}
        >
          <Image item={content} className={styles.headerImage} />
        </div>
      )}
      <div className={styles.titleContainer}>
        <h1 className="documentFirstHeading">{content.title}</h1>
      </div>

      {content.localtitle ? (
        <div className={styles.body}>
          <div className={styles.languageSelector}>
            <div className={styles.languageSelectorLabel}>
              {intl.formatMessage(messages.language_selector_label)}
            </div>
            <div className={styles.languageSelectorSelect}>
              <Select
                defaultSelectedKey={content.language?.title}
                placeholder={null}
                onSelectionChange={(key: string) => {
                  setSelectedLocale(key);
                }}
              >
                <Button
                  className={cx('react-aria-Button', styles.selectButton)}
                >
                  <SelectValue>
                    {({ defaultChildren }) => defaultChildren}
                  </SelectValue>
                  <span aria-hidden="true" className={styles.selectIcon}>
                    <ChevronDownSVG />
                  </span>
                </Button>
                <Popover isNonModal={true}>
                  <ListBox>
                    {languages.map((lang) => (
                      <ListBoxItem key={lang} id={lang}>
                        {lang}
                      </ListBoxItem>
                    ))}
                  </ListBox>
                </Popover>
              </Select>
            </div>
          </div>
          {((selectedLocale === content.language?.title && content.body) ||
            content.body_local_information) && (
            <div className={styles.bodyContent}>
              {selectedLocale === content.language?.title ? (
                <div dangerouslySetInnerHTML={{ __html: content?.body.data }} />
              ) : (
                <div
                  dangerouslySetInnerHTML={{
                    __html: content?.body_local_information.data,
                  }}
                />
              )}
            </div>
          )}
        </div>
      ) : (
        content?.body && (
          <div className={styles.description}>
            <div dangerouslySetInnerHTML={{ __html: content?.body?.data }} />
          </div>
        )
      )}

      {content.nation.length > 0 &&
        content.nation.map((nation) => (
          <div key={nation['@id']}>
            {representativeSections
              .filter((section) => section !== 'crm')
              .map((section) => (
                <CardsSection
                  content={nation}
                  section={section}
                  key={section}
                  bg="gray"
                />
              ))}
          </div>
        ))}

      <div className={styles.cardContainer}>
        {/* first base card */}
        <QubicaBaseCard
          key={content['@id']}
          content={content}
          bg="gray"
          main_base={true}
        />
        {/* second base card */}
        {content.base_title_2 && (
          <QubicaBaseCard
            key={content['@id']}
            content={content}
            bg="gray"
            main_base={false}
          />
        )}
      </div>

      <QubicaBaseFooter content={content} />
    </Container>
  );
};

export default QubicaBaseView;

const messages = defineMessages({
  language_selector_label: {
    id: 'language_selector_label',
    defaultMessage: 'Switch language',
  },
});
